import { createPrng } from '@play-co/replicant';
import type { State } from '../State';
import { getRandomInt, getRandomItemFromArray } from '../utils/random';
import {
  isRescueLevelOABucket,
  rescueLevelConfigs,
  rescueLevelFirstThemes,
  rescueLevelInterval,
  rescueLevelThemes,
  rescueLevelsPerSequence,
  type RescueLevelConfig,
  type RescueLevelOABucket,
} from './rescueLevel.ruleset';

export function rescueLevelGetOABucket(
  state: State,
): RescueLevelOABucket | undefined {
  const bucket = state.rescueLevel?.activeBucket;
  return isRescueLevelOABucket(bucket) ? bucket : undefined;
}

function rescueLevelGetAvailableSecondThemes(oaBucket: RescueLevelOABucket) {
  const firstTheme = rescueLevelFirstThemes[oaBucket];
  return rescueLevelThemes.filter((theme) => theme !== firstTheme);
}

function rescueLevelGetFirstLevel(state: State): number {
  return state.rescueLevel?.playedLevels?.[0] ?? state.match2.level;
}

function rescueLevelGetConfig(
  state: State,
  oaBucket: RescueLevelOABucket,
  rescueIndex: 0 | 1,
) {
  const firstLevel = rescueLevelGetFirstLevel(state);
  const random = createPrng(
    `rescue-level-configs-v1-${state.id}-${oaBucket}-${firstLevel}`,
  );
  const firstIndex = getRandomInt(0, rescueLevelConfigs.length - 1, random);
  if (rescueIndex === 0) return rescueLevelConfigs[firstIndex];

  const secondOffset = getRandomInt(1, rescueLevelConfigs.length - 1, random);
  const secondIndex = (firstIndex + secondOffset) % rescueLevelConfigs.length;
  return rescueLevelConfigs[secondIndex];
}

function rescueLevelGetSecondTheme(
  state: State,
  oaBucket: RescueLevelOABucket,
): (typeof rescueLevelThemes)[number] | undefined {
  const availableThemes = rescueLevelGetAvailableSecondThemes(oaBucket);

  // Resolve the roll only when the second rescue is due. A stable PRNG keeps
  // repeated getter calls from changing the active rescue during that level.
  const firstPlayedLevel = rescueLevelGetFirstLevel(state);
  const random = createPrng(
    `rescue-level-second-${state.id}-${oaBucket}-${firstPlayedLevel}`,
  );
  return getRandomItemFromArray(availableThemes, random);
}

/** Check whether an OA rescue sequence is active for the user. */
export function rescueLevelIsEnabled(state: State): boolean {
  const oaBucket = rescueLevelGetOABucket(state);
  if (!oaBucket) return false;

  const playedLevels = state.rescueLevel?.playedLevels ?? [];
  return playedLevels.length < rescueLevelsPerSequence;
}

/** Return the active rescue level configuration for the user. */
export function rescueLevelGetActive(
  state: State,
): RescueLevelConfig | undefined {
  const oaBucket = rescueLevelGetOABucket(state);
  if (!oaBucket) return undefined;

  const playedLevels = state.rescueLevel?.playedLevels ?? [];

  // A newly activated OA starts its first rescue immediately.
  if (!playedLevels.length) {
    return {
      ...rescueLevelGetConfig(state, oaBucket, 0),
      theme: rescueLevelFirstThemes[oaBucket],
    };
  }

  if (playedLevels.length >= rescueLevelsPerSequence) return undefined;

  const lastPlayedLevel = playedLevels[playedLevels.length - 1];
  if (state.match2.level !== lastPlayedLevel + rescueLevelInterval) {
    return undefined;
  }

  const theme = rescueLevelGetSecondTheme(state, oaBucket);
  return theme
    ? { ...rescueLevelGetConfig(state, oaBucket, 1), theme }
    : undefined;
}

/** Get the next Match-2 level that will have an active rescue level. */
export function rescueLevelGetNextLevel(state: State): number {
  const oaBucket = rescueLevelGetOABucket(state);
  if (!oaBucket) return 0;

  const playedLevels = state.rescueLevel?.playedLevels ?? [];

  if (playedLevels.length >= rescueLevelsPerSequence) return 0;

  // While the immediate first rescue is active, advertise its follow-up ten
  // Match-2 levels later.
  if (!playedLevels.length) {
    return state.match2.level + rescueLevelInterval;
  }

  const lastPlayedLevel = playedLevels[playedLevels.length - 1];
  const nextLevel = lastPlayedLevel + rescueLevelInterval;
  return nextLevel === state.match2.level ? 0 : nextLevel;
}
